/**
 * Cart Drawer Recommendations (Search & Discovery)
 * Card carousel with image, title, price, compare-at, and Add to cart.
 * All class names use prefix: pw-cart-drawer-
 */
class CartDrawerRecommendations extends HTMLElement {
  connectedCallback() {
    const productId = this.dataset.productId;
    const limit = this.dataset.limit || '4';
    const intent = this.dataset.intent || 'related';
    const baseUrl = (this.dataset.recommendationsUrl || '/recommendations/products').replace(/\/?$/, '');
    const url = `${baseUrl}.json?product_id=${encodeURIComponent(productId)}&limit=${encodeURIComponent(limit)}&intent=${encodeURIComponent(intent)}`;

    const loadingEl = this.querySelector('.pw-cart-drawer-recommendations-loading');
    const listEl = this.querySelector('.pw-cart-drawer-recommendations-list');

    if (!productId || !listEl) return;

    fetch(url)
      .then((response) => response.json())
      .then((data) => {
        if (loadingEl) loadingEl.hidden = true;
        const products = data.products || [];
        if (products.length === 0) {
          listEl.hidden = true;
          return;
        }
        listEl.hidden = false;
        listEl.innerHTML = '';
        listEl.appendChild(this.buildCarousel(products));
      })
      .catch(() => {
        if (loadingEl) loadingEl.hidden = true;
        listEl.hidden = true;
      });
  }

  buildCarousel(products) {
    const wrap = document.createElement('div');
    wrap.className = 'pw-cart-drawer-recommendations-carousel';

    const prevBtn = document.createElement('button');
    prevBtn.type = 'button';
    prevBtn.className = 'pw-cart-drawer-recommendations-arrow pw-cart-drawer-recommendations-arrow--prev';
    prevBtn.setAttribute('aria-label', 'Previous');
    prevBtn.innerHTML = '<span class="pw-cart-drawer-recommendations-arrow-icon">&#10094;</span>';

    const trackWrap = document.createElement('div');
    trackWrap.className = 'pw-cart-drawer-recommendations-track-wrap';
    const track = document.createElement('div');
    track.className = 'pw-cart-drawer-recommendations-track';

    products.forEach((product) => track.appendChild(this.buildCard(product)));
    trackWrap.appendChild(track);

    const nextBtn = document.createElement('button');
    nextBtn.type = 'button';
    nextBtn.className = 'pw-cart-drawer-recommendations-arrow pw-cart-drawer-recommendations-arrow--next';
    nextBtn.setAttribute('aria-label', 'Next');
    nextBtn.innerHTML = '<span class="pw-cart-drawer-recommendations-arrow-icon">&#10095;</span>';

    wrap.appendChild(prevBtn);
    wrap.appendChild(trackWrap);
    wrap.appendChild(nextBtn);

    const totalPages = Math.ceil(products.length / 2);
    let pageIndex = 0;
    const updateVisibility = () => {
      track.style.transform = `translateX(-${pageIndex * 100}%)`;
      prevBtn.classList.toggle('pw-cart-drawer-recommendations-arrow--disabled', pageIndex === 0);
      nextBtn.classList.toggle('pw-cart-drawer-recommendations-arrow--disabled', pageIndex >= totalPages - 1);
    };
    prevBtn.addEventListener('click', () => {
      if (pageIndex > 0) {
        pageIndex--;
        updateVisibility();
      }
    });
    nextBtn.addEventListener('click', () => {
      if (pageIndex < totalPages - 1) {
        pageIndex++;
        updateVisibility();
      }
    });
    updateVisibility();

    return wrap;
  }

  getOptionName(options, i) {
    if (!options || !options[i]) return `Option ${i + 1}`;
    const o = options[i];
    if (typeof o === 'string') return o;
    if (o && typeof o === 'object' && typeof o.name === 'string') return o.name;
    return `Option ${i + 1}`;
  }

  getProductOptions(product) {
    const options = product.options || [];
    const variants = product.variants || [];
    if (variants.length === 0) return [];
    return [0, 1, 2].map((i) => {
      const key = `option${i + 1}`;
      const values = [...new Set(variants.map((v) => v[key]).filter(Boolean))];
      return { name: this.getOptionName(options, i), key, values };
    }).filter((opt) => opt.values.length > 0);
  }

  findVariantByOptions(product, optionValues) {
    const variants = product.variants || [];
    return variants.find((v) => {
      return (optionValues[0] == null || v.option1 === optionValues[0]) &&
        (optionValues[1] == null || v.option2 === optionValues[1]) &&
        (optionValues[2] == null || v.option3 === optionValues[2]);
    }) || variants[0];
  }

  updateCardVariant(card, product, variant) {
    const price = variant ? (variant.price ?? product.price) : product.price;
    const compareAt = variant ? (variant.compare_at_price ?? null) : product.compare_at_price;
    const imgSrc = this.getVariantImage(product, variant);

    const priceEl = card.querySelector('.pw-cart-drawer-recommendations-card-price');
    const compareWrap = card.querySelector('.pw-cart-drawer-recommendations-card-prices');
    const compareEl = card.querySelector('.pw-cart-drawer-recommendations-card-compare');
    const img = card.querySelector('.pw-cart-drawer-recommendations-card-image');
    const addBtn = card.querySelector('.pw-cart-drawer-recommendations-card-add');

    if (priceEl) priceEl.textContent = this.formatMoneyWithCurrency(price);
    if (compareEl) {
      if (compareAt && compareAt > price) {
        compareEl.textContent = this.formatMoney(compareAt);
        compareEl.hidden = false;
      } else {
        compareEl.hidden = true;
      }
    } else if (compareAt && compareAt > price && compareWrap) {
      const newCompare = document.createElement('span');
      newCompare.className = 'pw-cart-drawer-recommendations-card-compare';
      newCompare.textContent = this.formatMoney(compareAt);
      compareWrap.appendChild(newCompare);
    }
    if (img && imgSrc) img.src = imgSrc;
    if (addBtn && variant) {
      addBtn.dataset.variantId = String(variant.id);
      addBtn.disabled = !variant.available;
      addBtn.textContent = variant.available ? 'Add to cart' : 'Sold out';
    }
  }

  buildCard(product) {
    const variants = product.variants || [];
    const variant = variants[0] || null;
    const variantId = variant ? variant.id : null;
    const price = variant ? (variant.price ?? product.price) : product.price;
    const compareAt = variant ? (variant.compare_at_price ?? null) : (product.compare_at_price ?? null);
    const imgSrc = this.getProductImage(product);
    const hasMultipleVariants = variants.length > 1;
    const productOptions = this.getProductOptions(product);

    const card = document.createElement('div');
    card.className = 'pw-cart-drawer-recommendations-card';
    card.dataset.productHandle = product.handle;

    const imgWrap = document.createElement('a');
    imgWrap.href = `/products/${product.handle}`;
    imgWrap.className = 'pw-cart-drawer-recommendations-card-image-wrap';
    const img = document.createElement('img');
    img.className = 'pw-cart-drawer-recommendations-card-image';
    img.alt = product.title;
    img.loading = 'lazy';
    if (imgSrc) {
      img.src = imgSrc;
    } else {
      img.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"%3E%3Crect fill="%23f0f0f0" width="100" height="100"/%3E%3C/svg%3E';
    }
    imgWrap.appendChild(img);

    const body = document.createElement('div');
    body.className = 'pw-cart-drawer-recommendations-card-body';

    const titleLink = document.createElement('a');
    titleLink.href = `/products/${product.handle}`;
    titleLink.className = 'pw-cart-drawer-recommendations-card-title';
    titleLink.textContent = product.title;
    body.appendChild(titleLink);

    if (hasMultipleVariants && productOptions.length > 0) {
      const pickerWrap = document.createElement('div');
      pickerWrap.className = 'pw-cart-drawer-recommendations-card-variants';
      productOptions.forEach((opt, optIndex) => {
        const row = document.createElement('div');
        row.className = 'pw-cart-drawer-recommendations-card-variant-row';
        const label = document.createElement('label');
        label.className = 'pw-cart-drawer-recommendations-variant-label';
        label.htmlFor = `pw-cart-drawer-option-${product.handle}-${optIndex}`;
        label.textContent = opt.name;
        const select = document.createElement('select');
        select.className = 'pw-cart-drawer-recommendations-variant-select';
        select.id = `pw-cart-drawer-option-${product.handle}-${optIndex}`;
        select.dataset.optionIndex = String(optIndex);
        opt.values.forEach((val) => {
          const option = document.createElement('option');
          option.value = val;
          option.textContent = val;
          if (variant && variant[opt.key] === val) option.selected = true;
          select.appendChild(option);
        });
        select.addEventListener('change', (e) => {
          e.stopPropagation();
          const optionValues = productOptions.map((_, i) => {
            const s = card.querySelector(`.pw-cart-drawer-recommendations-variant-select[data-option-index="${i}"]`);
            return s ? s.value : null;
          });
          const newVariant = this.findVariantByOptions(product, optionValues);
          this.updateCardVariant(card, product, newVariant);
        });
        row.appendChild(label);
        row.appendChild(select);
        pickerWrap.appendChild(row);
      });
      body.appendChild(pickerWrap);
    }

    const pricesRow = document.createElement('div');
    pricesRow.className = 'pw-cart-drawer-recommendations-card-prices';
    const priceEl = document.createElement('span');
    priceEl.className = 'pw-cart-drawer-recommendations-card-price';
    priceEl.textContent = this.formatMoneyWithCurrency(price);
    pricesRow.appendChild(priceEl);
    if (compareAt && compareAt > price) {
      const compareEl = document.createElement('span');
      compareEl.className = 'pw-cart-drawer-recommendations-card-compare';
      compareEl.textContent = this.formatMoney(compareAt);
      pricesRow.appendChild(compareEl);
    }
    body.appendChild(pricesRow);

    const addBtn = document.createElement('button');
    addBtn.type = 'button';
    addBtn.className = 'pw-cart-drawer-recommendations-card-add button button--primary';
    addBtn.textContent = variant && !variant.available ? 'Sold out' : 'Add to cart';
    if (variantId) {
      addBtn.dataset.variantId = String(variantId);
      addBtn.addEventListener('click', () => {
        const id = addBtn.dataset.variantId;
        if (id) this.addToCart(id, addBtn);
      });
    } else {
      addBtn.disabled = true;
    }
    if (variant && !variant.available) addBtn.disabled = true;
    body.appendChild(addBtn);

    card.appendChild(imgWrap);
    card.appendChild(body);
    card._product = product;
    return card;
  }

  getProductImage(product) {
    const fi = product.featured_image;
    if (fi) {
      if (typeof fi === 'string') return fi;
      if (fi.url) return fi.url;
      if (fi.src) return fi.src;
    }
    if (product.images && product.images[0]) {
      const img = product.images[0];
      return typeof img === 'string' ? img : (img.url || img.src);
    }
    const v = product.variants && product.variants[0];
    if (v && v.featured_image) {
      const vimg = v.featured_image;
      if (typeof vimg === 'string') return vimg;
      if (vimg.url) return vimg.url;
      if (vimg.src) return vimg.src;
    }
    return null;
  }

  getVariantImage(product, variant) {
    if (variant && variant.featured_image) {
      const vimg = variant.featured_image;
      if (typeof vimg === 'string') return vimg;
      if (vimg.url) return vimg.url;
      if (vimg.src) return vimg.src;
    }
    return this.getProductImage(product);
  }

  addToCart(variantId, buttonEl) {
    if (buttonEl.getAttribute('aria-disabled') === 'true') return;
    buttonEl.setAttribute('aria-disabled', 'true');
    const originalText = buttonEl.textContent;
    buttonEl.textContent = 'Adding…';

    const body = JSON.stringify({
      items: [{ id: variantId, quantity: 1 }],
      sections: ['cart-drawer', 'cart-icon-bubble'],
      sections_url: window.location.pathname,
    });

    fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body,
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status && data.status !== 200) {
          buttonEl.removeAttribute('aria-disabled');
          buttonEl.textContent = originalText;
          return;
        }
        if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-drawer-recommendations',
            cartData: data,
          });
        }
      })
      .then(() => {
        buttonEl.removeAttribute('aria-disabled');
        buttonEl.textContent = originalText;
      })
      .catch(() => {
        buttonEl.removeAttribute('aria-disabled');
        buttonEl.textContent = originalText;
      });
  }

  formatMoney(cents) {
    const format = this.dataset.moneyFormat || '${{amount}}';
    const value = (cents / 100).toFixed(2);
    return format.replace(/\{\{\s*amount\s*\}\}/, value).replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100).toString());
  }

  formatMoneyWithCurrency(cents) {
    const format = this.dataset.moneyWithCurrencyFormat || this.dataset.moneyFormat || '${{amount}} USD';
    const value = (cents / 100).toFixed(2);
    return format.replace(/\{\{\s*amount\s*\}\}/, value).replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100).toString());
  }
}

customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
